// Confirm modal: shows AI-extracted cheque fields, editable, then saves.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAccounts } from "@/lib/useAccounts";

// Stable module-scope input row — defined outside so it is NOT re-created
// on every parent state change (previous inline definition made the input
// lose focus after each keystroke, closing the keyboard on mobile).
function Field({ k, label, type = "text", value, onChange, colSpan = 1 }) {
  return (
    <label className={colSpan === 2 ? "col-span-2 block" : "block"}>
      <span className="text-xs font-bold text-slate-500">{label}</span>
      <input data-testid={`chq-${k}`} type={type} value={value} onChange={onChange}
        className="mt-1 w-full h-11 rounded-xl neu-inset px-3 text-sm bg-transparent outline-none" />
    </label>
  );
}

export function ChequeConfirmModal({ open, parsed, suggestedAccountId, direction = "outgoing", onClose, onSaved, onScanAgain }) {
  const { t, lang } = useI18n();
  const accounts = useAccounts();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const isIncoming = direction === "incoming";
  const partyLabel = isIncoming ? (lang === "ar" ? "الساحب (من)" : "Drawer (from)") : t("payee");
  const partyKey = isIncoming ? "drawer" : "payee";

  useEffect(() => {
    if (!parsed) return;
    // OCR primarily fills "payee" — treat that as drawer when incoming.
    const partyValue = parsed.payee || parsed.drawer || "";
    setF({
      cheque_number: parsed.cheque_number || "",
      payee: isIncoming ? "" : partyValue, drawer: isIncoming ? partyValue : "",
      amount: parsed.amount || "", currency: (parsed.currency || "AED").toUpperCase(),
      due_date: parsed.due_date || new Date().toLocaleDateString("en-CA"),
      bank_name: parsed.bank_name || "", purpose: parsed.purpose || "",
      account_id: suggestedAccountId || "",
    });
  }, [parsed, suggestedAccountId, isIncoming]);

  if (!open) return null;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!f[partyKey] || !f.amount || !f.due_date) return toast.error(t("error"));
    setBusy(true);
    try {
      const payload = { ...f, amount: Number(f.amount), direction,
        payee: isIncoming ? (f.drawer || "—") : f.payee };
      await api.post("/cheques", payload);
      toast.success(t("chequeSaved"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="cheque-confirm-modal" className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm grid place-items-end sm:place-items-center p-3" onClick={onClose}>
      <div className="w-full sm:max-w-md neu p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-extrabold text-lg neu-accent-blue">{t("chequeConfirmTitle")}</h3>
        <p className="text-xs text-slate-500 mt-1 mb-4">{t("chequeConfirmSub")}</p>
        <div className="grid grid-cols-2 gap-3">
          <Field k={partyKey} label={partyLabel} value={f[partyKey] || ""} onChange={set(partyKey)} />
          <Field k="amount" type="number" label={t("amount")} value={f.amount || ""} onChange={set("amount")} />
          <Field k="currency" label={t("currency")} value={f.currency || ""} onChange={set("currency")} />
          <Field k="due_date" type="date" label={t("dueDate")} value={f.due_date || ""} onChange={set("due_date")} />
          <Field k="cheque_number" label={t("chequeNumber")} value={f.cheque_number || ""} onChange={set("cheque_number")} />
          <Field k="bank_name" label={t("bankName")} value={f.bank_name || ""} onChange={set("bank_name")} />
          <Field k="purpose" label={t("purpose")} value={f.purpose || ""} onChange={set("purpose")} colSpan={2} />
          {(accounts.items || []).length > 0 && (
            <label className="col-span-2">
              <span className="text-xs font-bold text-slate-500">{t("accounts")}</span>
              <select data-testid="chq-account" value={f.account_id || ""} onChange={set("account_id")}
                className="mt-1 w-full h-11 rounded-xl neu-inset px-3 text-sm bg-transparent outline-none">
                <option value="">—</option>
                {accounts.items.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
          )}
        </div>
        <div className="flex gap-2 mt-5">
          <button data-testid="chq-scan-again" onClick={onScanAgain} className="w-11 h-11 rounded-xl neu-btn grid place-items-center"><RefreshCw className="w-4 h-4" /></button>
          <button data-testid="chq-cancel" onClick={onClose} className="flex-1 h-11 rounded-xl neu-btn font-bold flex items-center justify-center gap-2"><X className="w-4 h-4" />{t("cancel")}</button>
          <button data-testid="chq-save" onClick={save} disabled={busy} className="flex-1 h-11 rounded-xl neu-fill-blue text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60"><Check className="w-4 h-4" />{t("save")}</button>
        </div>
      </div>
    </div>
  );
}
